import { InsightsChannels, PromoRow, ShareRow, StoreRow } from '../../types/marketing';
import DataTable, { DataTableColumn } from './DataTable';
import InstallsSplitChart from './InstallsSplitChart';
import { DASH, formatInt } from './marketingMath';

const PROMO_COLUMNS: DataTableColumn<PromoRow>[] = [
  { key: 'code', header: 'Code', accessor: (r) => r.code, format: 'text' },
  { key: 'holdersClaimed', header: 'Claimed', accessor: (r) => r.holdersClaimed, format: 'int' },
  { key: 'redemptions', header: 'Redemptions', accessor: (r) => r.redemptions, format: 'int' },
  { key: 'discountAed', header: 'Discount (AED)', accessor: (r) => r.discountAed, format: 'aed' },
  { key: 'paidAed', header: 'Paid (AED)', accessor: (r) => r.paidAed, format: 'aed' },
];

const SHARE_COLUMNS: DataTableColumn<ShareRow>[] = [
  { key: 'channel', header: 'Channel', accessor: (r) => r.channel, format: 'text' },
  { key: 'sent', header: 'Sent', accessor: (r) => r.sent, format: 'int' },
  { key: 'delivered', header: 'Delivered', accessor: (r) => r.delivered, format: 'int' },
  { key: 'read', header: 'Read', accessor: (r) => r.read, format: 'int' },
  { key: 'failed', header: 'Failed', accessor: (r) => r.failed, format: 'int' },
];

const STORE_NAMES: Record<string, string> = { 'app-store': 'App Store', 'google-play': 'Google Play' };
const SHARE_TAP_NAMES: Record<string, string> = { app_share: 'Shared from the app', admin_share: 'Shared by admins' };

function StoreCard({ s }: { s: StoreRow }) {
  const connected = s.status !== 'NOT_CONNECTED' && s.status !== 'NEVER';
  return (
    <div className="mkt-store" data-testid={`store-card-${s.store}`} data-status={s.status}>
      <h4 className="mkt-card__title">{STORE_NAMES[s.store] ?? s.store}</h4>
      {connected ? (
        <dl className="mkt-store__facts">
          <div><dt>Product page views</dt><dd>{formatInt(s.productPageViews)}</dd></div>
          <div><dt>Installs</dt><dd>{formatInt(s.installs)}</dd></div>
          <div><dt>Attributed installs</dt><dd>{formatInt(s.installsAttributed)}</dd></div>
          <div><dt>Rating</dt><dd>{s.ratingAvg == null ? DASH : `${s.ratingAvg.toFixed(2)} (${formatInt(s.ratingCount)})`}</dd></div>
        </dl>
      ) : (
        <p className="mkt-store__off"><span className="mkt-chip mkt-chip--off">Not connected</span>{s.consoleHint && <span className="mkt-store__hint">{s.consoleHint}</span>}</p>
      )}
      {s.status === 'FAILED' && <p className="mkt-field-error">The last pull failed; numbers may be stale.</p>}
    </div>
  );
}

/** Spec 084 Channels tab — promo codes, share messages and their taps, the two stores, installs attributed vs other. */
export default function ChannelsSection({ data, range }: { data: InsightsChannels; range: { from: string; to: string; timezone: string } }) {
  return (
    <div className="mkt-section" data-testid="section-channels">
      <section className="mkt-card">
        <DataTable caption="Promo codes" columns={PROMO_COLUMNS} rows={data.promoCodes} rowKey={(r) => r.code}
          defaultSort={{ key: 'redemptions', dir: 'desc' }} emptyText="No promo code use in this range" exportMeta={range} testId="channels-promo" />
      </section>
      <section className="mkt-card">
        <DataTable caption="Shares" columns={SHARE_COLUMNS} rows={data.shares} rowKey={(r) => r.channel}
          defaultSort={{ key: 'sent', dir: 'desc' }} emptyText="No share messages in this range" exportMeta={range} testId="channels-shares" />
        <p className="mkt-foot" data-testid="channels-share-taps">
          Store taps from shared links: {Object.entries(data.shareTaps).map(([k, v]) => `${SHARE_TAP_NAMES[k] ?? k} ${formatInt(v)}`).join(' · ') || DASH}
        </p>
      </section>
      <section className="mkt-card" aria-label="Stores">
        <div className="mkt-stores">{data.stores.map((s) => <StoreCard key={s.store} s={s} />)}</div>
      </section>
      <section className="mkt-card"><InstallsSplitChart rows={data.installsSplit} /></section>
    </div>
  );
}
